import React from 'react';
import PropTypes from 'prop-types';
import { ToggleButtonGroup, ToggleButton } from '@mui/material';
// Components
import SelectDropdown from '../../components/SelectDropdown';
import Table from '../../components/Table';
import NoData from '../../components/NoData';
import { ShimmerComponent } from '../../components/Shimmer/ShimmerComponent';
import BouncingLogo from '../../components/Shimmer/BouncingLogo';
import ReportDownloadComponent from '../../components/DownloadReport/ReportDownloadComponent';
// Custom hooks
import useCurrentPlanSpendData from './hooks/useCurrentPlanSpendData';
// Constants
import {
  CURRENT_PLAN_DOWNLOAD_FILE_MONTHLY,
  CURRENT_PLAN_DOWNLOAD_FILE_WEEKLY,
  CURRENT_PLAN_WEEKLY_SPEND,
  CURRENT_PLAN_MONTHLY_SPEND,
  CURRENT_PLAN_WEEKLY_HEADERS,
} from './constants/CurrentPlanSpendConstants';
// Styles
import { styles } from './styles/currentPlanSpendStyles';

const CurrentPlanSpend = ({
  WEEKLY_SPEND_COMPONENT = CURRENT_PLAN_WEEKLY_SPEND,
  MONTHLY_SPEND_COMPONENT = CURRENT_PLAN_MONTHLY_SPEND,
  WEEKLY_HEADERS_COMPONENT = CURRENT_PLAN_WEEKLY_HEADERS,
  title = 'Current Plan Spend',
  DOWNLOAD_FILE_NAME_WEEKLY = CURRENT_PLAN_DOWNLOAD_FILE_WEEKLY,
  DOWNLOAD_FILE_NAME_MONTHLY = CURRENT_PLAN_DOWNLOAD_FILE_MONTHLY,
}) => {
  // Use the custom hook for all data and logic
  const {
    // Data and loading states
    weeklyTableHeaders,
    weeklyRowData,
    monthlyTableHeaders,
    monthlyRowData,
    errors,
    weeklyLoading,
    headersLoading,
    monthlyLoading,
    initialLoading,
    // Filter states
    planSpendView,
    resetDropdown,
    // Filter data
    updatedChannelList,
    options,
    // Channel selection state and logic
    modifiedRowData,
    modifiedMonthlyRowData,
    noDataForSelectedChannel,
    showNoChannelSelected,
    // Handler functions
    getDataForStoreId,
    handlePlanSpendViewChange,
    changeSelectedFilters,
    // Download props
    getDownloadProps,
  } = useCurrentPlanSpendData({
    WEEKLY_SPEND_COMPONENT,
    MONTHLY_SPEND_COMPONENT,
    WEEKLY_HEADERS_COMPONENT,
    DOWNLOAD_FILE_NAME_WEEKLY,
    DOWNLOAD_FILE_NAME_MONTHLY,
  });

  // Get download props directly from the hook
  const downloadProps = getDownloadProps();

  // Define renderView as a pure function that doesn't modify state
  const renderCurrentView = () => {
    // Use initialLoading to show loader immediately on page load
    const isLoading = initialLoading || weeklyLoading || headersLoading || monthlyLoading;

    // If we're in initial loading state, show loader immediately
    if (isLoading) {
      return (
        <ShimmerComponent updatedStyle={styles.shimmerComponentUpdatedStyle}>
          <BouncingLogo height="72vh" text="Loading..." />
        </ShimmerComponent>
      );
    }

    // Determine which rows to use based on view type and data availability
    let rows = [];

    if (planSpendView === 'Weekly') {
      rows = modifiedRowData.length ? modifiedRowData : weeklyRowData;
    } else {
      rows = modifiedMonthlyRowData.length ? modifiedMonthlyRowData : monthlyRowData;
    }

    const columns = planSpendView === 'Weekly' ? weeklyTableHeaders : monthlyTableHeaders;

    // Handle no channel selected or no data for selected channel
    if ((showNoChannelSelected || noDataForSelectedChannel) && !isLoading) {
      return (
        <div style={styles.noDataContainer}>
          <NoData
            textValue={
              noDataForSelectedChannel
                ? 'No data available for selected channel'
                : 'No Channel selected'
            }
          />
        </div>
      );
    }

    // Handle errors
    if (errors.weekly_data_error || errors.headers_error || errors.monthly_data_error) {
      return <NoData textValue="Error Loading data" />;
    }

    // Handle data display
    if (rows?.length > 1 && columns?.length >= 4) {
      return (
        <div style={styles.tableContainer}>
          <Table
            boldHeaders={true}
            data={rows}
            stickyColumns={['channel', 'audience', 'total']}
            columns={columns}
          />
        </div>
      );
    }

    // No data case
    return (
      <div style={styles.noDataContainer}>
        <NoData textValue="No Data Available" />
      </div>
    );
  };

  return (
    <div style={styles.container}>
      <div style={styles.titleWrapper}>
        <div style={styles.titleContainer}>
          <span>{title}</span>
        </div>
      </div>
      <div style={styles.filtersWrapper}>
        <div style={styles.filtersContainer}>
          <SelectDropdown
            options={options}
            label={'Select Branch'}
            handleFilterChange={getDataForStoreId}
            singleSelectDefaultValue={options[0]?.value}
            resetValues={resetDropdown}
            marginExist={false}
            disabled={initialLoading || weeklyLoading || headersLoading || monthlyLoading}
          />
          <SelectDropdown
            options={updatedChannelList}
            label={'Select Channel'}
            allOptionsSelectedText={'All Channels'}
            multiple={true}
            isSelectAll={true}
            multiSelectDefaultValues={updatedChannelList?.map((channel) => channel?.value) || []}
            handleFilterChange={changeSelectedFilters}
            resetValues={resetDropdown}
            marginExist={false}
            disabled={initialLoading || weeklyLoading || headersLoading || monthlyLoading}
          />
        </div>
        <ToggleButtonGroup
          value={planSpendView}
          onChange={handlePlanSpendViewChange}
          exclusive
          aria-label="Toggle Spends And Opps Table View"
          sx={{ paddingTop: '10px' }}
        >
          <ToggleButton
            value={'Weekly'}
            style={styles.toggleButton(planSpendView === 'Weekly')}
            disabled={initialLoading || weeklyLoading || headersLoading || monthlyLoading}
          >
            Weekly
          </ToggleButton>
          <ToggleButton
            value={'Monthly'}
            style={styles.toggleButton(planSpendView === 'Monthly')}
            disabled={initialLoading || weeklyLoading || headersLoading || monthlyLoading}
          >
            Monthly
          </ToggleButton>
        </ToggleButtonGroup>
        <ReportDownloadComponent {...downloadProps} />
      </div>
      <div>
        <h3 style={styles.heading}>{`${planSpendView} ${title}`}</h3>
        {renderCurrentView()}
      </div>
    </div>
  );
};

CurrentPlanSpend.propTypes = {
  WEEKLY_SPEND_COMPONENT: PropTypes.string,
  MONTHLY_SPEND_COMPONENT: PropTypes.string,
  WEEKLY_HEADERS_COMPONENT: PropTypes.string,
  title: PropTypes.string,
  DOWNLOAD_FILE_NAME_WEEKLY: PropTypes.string,
  DOWNLOAD_FILE_NAME_MONTHLY: PropTypes.string,
};

export default CurrentPlanSpend;
